import React__default, { createContext, useContext, useMemo } from 'react';
import { E as ErrorBoundary } from './vendor-8000eab9.js';
import { FallbackComponent } from '@sm/question-ui/helpers';
import { getTypeId } from '@sm/question-definitions';

const filterVisualizationByQuestionType = (typeId) => (qd) => {
    const [qts] = qd;
    return qts.includes(typeId);
};
const getAnswerLayoutColumns = (answerLayout) => {
    if (answerLayout === 'VERTICAL') {
        return 1;
    }
    if (answerLayout === 'TWO_COLUMNS') {
        return 2;
    }
    if (answerLayout === 'THREE_COLUMNS') {
        return 3;
    }
    return 'horizontal';
};
/**
 * Trim leading and trailing whitespace from text inputs
 */
const trimAllValues = (values) => values.map((value) => ({
    ...value,
    value: value.value.trim(),
}));
/**
 * Trim leading and trailing whitespace from "other" text inputs - for both `ANSWER` and `COMMENT`
 */
const trimOtherValues = (values) => values.map((value) => {
    if (value.type !== 'ANSWER' && value.type !== 'COMMENT') {
        return value;
    }
    return {
        ...value,
        value: value.value.trim(),
    };
});
/**
 * Trim leading and trailing whitespace from DateTimeAnswerType
 */
const trimDateTimeValues = (values) => values.map((response) => {
    return {
        ...response,
        value: {
            date: response.value.date?.trim(),
            hour: response.value.hour?.trim(),
            minute: response.value.minute?.trim(),
            period: response.value.period?.trim(),
        },
    };
});

const declareVisualization = (questionTypes, capabilities, component) => {
    return [questionTypes, capabilities, component];
};
const providerAccessContext = createContext(null);
const useProviderAccessContext = () => {
    const access = useContext(providerAccessContext);
    if (!access) {
        throw new Error('Must call useProviderAccessContext inside a properly setup Capability Provider');
    }
    const { useUpstreamConfig, useVisualizationList } = access;
    return {
        useVisualizationList,
        useUpstreamConfig,
    };
};
/** providerFactory
 * Creates a helper component that enables a CapabilityProvider to store the appropriate configurations,
 * making them available to the visualizations through the generic controllers. It makes sure that all
 * Visualizations have the proper types passed onto them.
 * @param visualizationList list of visualization definitions for all the visualizations that will be
 * supported by this Capability Provider.
 * @returns React Context Provider accepting the appropriate configuration for the CapabilityProvider
 */
const providerFactory = (visualizationList) => {
    const ctx = React__default.createContext(null);
    // TODO: further filter types to match the ones in the right indexes based on capability
    const useVisualizationList = (capability) => visualizationList.filter(([, fs]) => fs.indexOf(capability) >= 0);
    const useUpstreamConfig = () => {
        const config = useContext(ctx);
        if (!config) {
            throw new Error('Must call useUpstreamConfig inside a properly set up Capability Provider');
        }
        return config;
    };
    const providerValue = {
        useVisualizationList: useVisualizationList,
        useUpstreamConfig: useUpstreamConfig,
    };
    return function ProvidersWrapper({ children, value }) {
        return (React__default.createElement(ErrorBoundary, { FallbackComponent: FallbackComponent },
            React__default.createElement(providerAccessContext.Provider, { value: providerValue },
                React__default.createElement(ctx.Provider, { value: value }, children))));
    };
};
function UnsupportedVisualization({ question: { typeId }, }) {
    return React__default.createElement("div", null,
        "Unsupported Visualization for ",
        typeId);
}
const createVisualization = (capability, questionTypeId, visualizationOptions, upstreamActions, settings, renderProps) => {
    const [, , VisualizationComponent] = visualizationOptions.find(filterVisualizationByQuestionType(questionTypeId)) || [];
    if (!VisualizationComponent) {
        return function UnsupportedVisualizationWrapper({ question }) {
            return React__default.createElement(UnsupportedVisualization, { question: question });
        };
    }
    return function VisualizationComponentWrapper({ question, displayOptions, namedSlots, toExternalQuestion, }) {
        return (React__default.createElement(VisualizationComponent, { capability: capability, question: question, upstreamActions: upstreamActions, settings: settings, displayOptions: displayOptions, renderProps: renderProps, toExternalQuestion: toExternalQuestion, namedSlots: namedSlots }));
    };
};

// TODO: add to question type definitions
const isRequired = (q) => q.required?.type !== undefined;

const equals = (a, b) => {
    if (a === b) {
        return true;
    }
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }
    if (!a || !b || (typeof a !== 'object' && typeof b !== 'object')) {
        return a === b;
    }
    if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) {
        return false;
    }
    const objA = a;
    const objB = b;
    const keys = Object.keys(objA);
    if (keys.length !== Object.keys(objB).length) {
        return false;
    }
    return keys.every((k) => equals(objA[k], objB[k]));
};

const equalsIgnoreOrder = (a, b) => {
    if (a.length !== b.length) {
        return false;
    }
    const uniqueValues = new Set([...a, ...b]);
    // eslint-disable-next-line no-restricted-syntax
    for (const v of uniqueValues) {
        const aCount = a.filter((e) => equals(e, v)).length;
        const bCount = b.filter((e) => equals(e, v)).length;
        if (aCount !== bCount) {
            return false;
        }
    }
    return true;
};

/** Finds and attaches a typeId to an `ExternalQuestion` */
const useExternalQuestionAugmentation = (externalQuestion, responseSet) => {
    return useMemo(() => {
        const typeId = getTypeId(externalQuestion);
        if (!typeId) {
            throw new Error(`Unsupported Question Type of family ${externalQuestion.family}, variant ${externalQuestion.variant}.`);
        }
        if (responseSet !== undefined) {
            return {
                ...externalQuestion,
                typeId,
                ...responseSet,
            };
        }
        return { ...externalQuestion, typeId };
    }, [externalQuestion, responseSet]);
};

export { useExternalQuestionAugmentation as a, trimAllValues as b, createVisualization as c, declareVisualization as d, equalsIgnoreOrder as e, trimDateTimeValues as f, getAnswerLayoutColumns as g, isRequired as i, providerFactory as p, trimOtherValues as t, useProviderAccessContext as u };
//# sourceMappingURL=utils-7074a272.js.map
